import React from "react";
import {
  File,
  FileText,
  Image as ImageIcon,
  Film,
  Music,
  Archive,
  Code,
  Folder,
  FormInput,
} from "lucide-react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { FileNode } from "./types";

/**
 * Get icon for file type
 */
export function getFileIcon(node: FileNode, className: string = "w-5 h-5") {
  if (node.type === "folder") {
    return <Folder className={`${className} text-blue-500`} />;
  }

  if (node.type === "form_submission") {
    return <FormInput className={`${className} text-purple-500`} />;
  }

  const ext = node.ext?.toLowerCase() || "";
  const mime = node.mimeType?.toLowerCase() || "";

  if (
    mime.startsWith("image/") ||
    ["jpg", "jpeg", "png", "gif", "svg", "webp"].includes(ext)
  ) {
    return <ImageIcon className={`${className} text-purple-500`} />;
  }

  if (mime.startsWith("video/") || ["mp4", "mov", "avi", "mkv"].includes(ext)) {
    return <Film className={`${className} text-red-500`} />;
  }

  if (mime.startsWith("audio/") || ["mp3", "wav", "ogg", "m4a"].includes(ext)) {
    return <Music className={`${className} text-pink-500`} />;
  }

  if (["zip", "rar", "tar", "gz", "7z"].includes(ext)) {
    return <Archive className={`${className} text-orange-500`} />;
  }

  if (
    [
      "js",
      "ts",
      "tsx",
      "jsx",
      "py",
      "java",
      "cpp",
      "c",
      "go",
      "rs",
      "html",
      "css",
      "json",
      "xml",
    ].includes(ext)
  ) {
    return <Code className={`${className} text-green-500`} />;
  }

  if (["pdf", "doc", "docx", "txt", "md"].includes(ext)) {
    return (
      <FileText className={`${className} text-gray-600 dark:text-gray-400`} />
    );
  }

  return <File className={`${className} text-gray-500`} />;
}

/**
 * Format file size
 */
export function formatFileSize(bytes?: number): string {
  if (!bytes) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * Format date
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } else if (diffDays === 1) {
    return "Yesterday";
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    return date.toLocaleDateString();
  }
}

export interface FileCardProps {
  node: FileNode;
  selected: boolean;
  onClick: (e: React.MouseEvent) => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onDoubleClick?: () => void;
  uploadProgress?: number;
}

/**
 * Grid view card for files/folders
 */
export const FileCard = React.forwardRef<HTMLDivElement, FileCardProps>(
  (
    { node, selected, onClick, onContextMenu, onDoubleClick, uploadProgress },
    ref
  ) => {
    // Make this item draggable
    const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
      id: node.id,
    });

    // Make folders droppable
    const { setNodeRef: setDropRef, isOver } = useDroppable({
      id: node.id,
      disabled: node.type !== "folder",
    });

    // Combine refs
    const combinedRef = (el: HTMLDivElement | null) => {
      setDragRef(el);
      setDropRef(el);
      if (typeof ref === "function") {
        ref(el);
      } else if (ref) {
        ref.current = el;
      }
    };

    return (
      <div
        ref={combinedRef}
        className={`
          relative group cursor-pointer rounded-xl p-4
          border-2 transition-all
          ${
            selected
              ? "border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-950/30"
              : isOver && node.type === "folder"
              ? "border-blue-400 dark:border-blue-500 bg-blue-100 dark:bg-blue-900/40"
              : "border-transparent hover:border-gray-300 dark:hover:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-900/50"
          }
          ${isDragging ? "opacity-50" : ""}
        `}
        data-file-id={node.id}
      >
        {/* Invisible drag handle that covers the entire card */}
        <div
          {...listeners}
          {...attributes}
          onClick={onClick}
          onContextMenu={onContextMenu}
          onDoubleClick={onDoubleClick}
          className="absolute inset-0 z-0"
          style={{ touchAction: 'none' }}
        />

        {/* Content with higher z-index so it's visible */}
        <div className="relative z-10 pointer-events-none">
        {/* Thumbnail or Icon */}
        <div className="flex items-center justify-center h-24 mb-3">
          {node.thumbnailUrl ? (
            <img
              src={node.thumbnailUrl}
              alt={node.name}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          ) : (
            getFileIcon(node, "w-16 h-16")
          )}
        </div>

        {/* File name */}
        <div className="text-center">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate px-1">
            {node.name}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {node.type === "folder" ? "Folder" : formatFileSize(node.size)}
          </p>
        </div>

        {/* Upload progress */}
        {uploadProgress !== undefined && uploadProgress < 100 && (
          <div className="absolute inset-0 bg-white/90 dark:bg-gray-900/90 rounded-xl flex items-center justify-center">
            <div className="w-3/4">
              <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-xs text-center text-gray-600 dark:text-gray-400 mt-2">
                {uploadProgress}%
              </p>
            </div>
          </div>
        )}

        {/* Selection indicator */}
        {selected && (
          <div className="absolute top-2 right-2 w-5 h-5 bg-blue-500 dark:bg-blue-400 rounded-full flex items-center justify-center">
            <svg
              className="w-3 h-3 text-white"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        )}
        </div>
      </div>
    );
  }
);

FileCard.displayName = "FileCard";

export interface FileRowProps {
  node: FileNode;
  selected: boolean;
  onClick: (e: React.MouseEvent) => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onDoubleClick?: () => void;
  uploadProgress?: number;
}

/**
 * List view row for files/folders
 */
export const FileRow = React.forwardRef<HTMLDivElement, FileRowProps>(
  (
    { node, selected, onClick, onContextMenu, onDoubleClick, uploadProgress },
    ref
  ) => {
    // Make this item draggable
    const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
      id: node.id,
    });

    // Make folders droppable
    const { setNodeRef: setDropRef, isOver } = useDroppable({
      id: node.id,
      disabled: node.type !== "folder",
    });

    // Combine refs
    const combinedRef = (el: HTMLDivElement | null) => {
      setDragRef(el);
      setDropRef(el);
      if (typeof ref === "function") {
        ref(el);
      } else if (ref) {
        ref.current = el;
      }
    };

    return (
      <div
        ref={combinedRef}
        className={`
          relative flex items-center gap-3 px-4 py-3 cursor-pointer
          border-l-2 transition-all
          ${
            selected
              ? "border-l-blue-500 dark:border-l-blue-400 bg-blue-50 dark:bg-blue-950/30"
              : isOver && node.type === "folder"
              ? "border-l-blue-400 dark:border-l-blue-500 bg-blue-100 dark:bg-blue-900/40"
              : "border-l-transparent hover:bg-gray-50 dark:hover:bg-gray-900/50"
          }
          ${isDragging ? "opacity-50" : ""}
        `}
        data-file-id={node.id}
      >
        {/* Invisible drag handle */}
        <div
          {...listeners}
          {...attributes}
          onClick={onClick}
          onContextMenu={onContextMenu}
          onDoubleClick={onDoubleClick}
          className="absolute inset-0 z-0"
          style={{ touchAction: 'none' }}
        />

        {/* Content with higher z-index */}
        <div className="relative z-10 flex items-center gap-3 flex-1 pointer-events-none">
          {/* Icon */}
          <div className="flex-shrink-0">{getFileIcon(node, "w-5 h-5")}</div>

          {/* Name */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {node.name}
            </p>
          </div>

          {/* Size */}
          <div className="w-24 text-sm text-gray-500 dark:text-gray-400 text-right">
            {node.type === "file" ? formatFileSize(node.size) : "-"}
          </div>

          {/* Date */}
          <div className="w-32 text-sm text-gray-500 dark:text-gray-400 text-right">
            {formatDate(node.updatedAt)}
          </div>
        </div>

        {/* Upload progress */}
        {uploadProgress !== undefined && uploadProgress < 100 && (
          <div className="absolute inset-0 bg-white/90 dark:bg-gray-900/90 flex items-center px-4">
            <div className="flex-1 flex items-center gap-3">
              <div className="flex-1">
                <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
              <span className="text-xs text-gray-600 dark:text-gray-400 w-12 text-right">
                {uploadProgress}%
              </span>
            </div>
          </div>
        )}
      </div>
    );
  }
);

FileRow.displayName = "FileRow";
